import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AltalabLogo } from "../components/AltalabLogo";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  ShoppingBag,
  RefreshCw,
  Star,
  MapPin,
  Heart,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Lock,
  ArrowRight,
  X,
  Sparkles,
  TrendingUp,
  Shield,
  CheckCircle,
  Filter,
  ChevronDown,
  Crown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform icons
const TelegramIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const PrivacyIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
  </svg>
);

const getPlatformIcon = (platform) => {
  switch (platform?.toLowerCase()) {
    case 'telegram': return <TelegramIcon />;
    case 'privacy': return <PrivacyIcon />;
    default: return <ShoppingBag className="w-4 h-4" />;
  }
};

const getPlatformColor = (platform) => {
  switch (platform?.toLowerCase()) {
    case 'telegram': return 'text-[#26A5E4] bg-[#26A5E4]/10';
    case 'privacy': return 'text-emerald-400 bg-emerald-400/10';
    case 'onlyfans': return 'text-[#00AFF0] bg-[#00AFF0]/10';
    default: return 'text-violet-400 bg-violet-400/10';
  }
};

// Filter options
const PLATFORMS = [
  { value: "", label: "Todas" },
  { value: "privacy", label: "Privacy" },
  { value: "onlyfans", label: "OnlyFans" },
  { value: "telegram", label: "Telegram" },
  { value: "closefans", label: "CloseFans" },
  { value: "instagram", label: "Instagram" },
  { value: "twitter", label: "X/Twitter" },
  { value: "tiktok", label: "TikTok" },
];

const SUBSCRIBER_RANGES = [
  { value: "", label: "Todos" },
  { value: "0-10000", label: "Até 10k" },
  { value: "10000-50000", label: "10k - 50k" },
  { value: "50000-100000", label: "50k - 100k" },
  { value: "100000-", label: "+100k" },
];

const PROFILE_TYPES = [
  { value: "", label: "Todos" },
  { value: "vip,pago", label: "VIP/Pago" },
  { value: "free", label: "Free" },
];

// Helper to parse reach string to number
const parseReachNumber = (reach) => {
  if (!reach) return 0;
  const str = reach.toLowerCase();
  const match = str.match(/(\d+(?:[.,]\d+)?)\s*(k|m)?/);
  if (!match) return 0;
  let num = parseFloat(match[1].replace(',', '.'));
  if (match[2] === 'k') num *= 1000;
  if (match[2] === 'm') num *= 1000000;
  return num;
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [activeTab, setActiveTab] = useState("marketplace");
  const [services, setServices] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters state
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterSubscribers, setFilterSubscribers] = useState("");
  const [filterType, setFilterType] = useState("");
  
  // Login modal state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [registerName, setRegisterName] = useState("");
  
  // Forgot password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  
  // Pending action after login
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Apply filters when they change
  useEffect(() => {
    if (allServices.length === 0) return;
    
    let filtered = [...allServices];
    
    // Filter by platform
    if (filterPlatform) {
      filtered = filtered.filter(s => s.platform?.toLowerCase() === filterPlatform);
    }
    
    // Filter by subscriber range
    if (filterSubscribers) {
      const [min, max] = filterSubscribers.split('-').map(v => v ? parseInt(v) : null);
      filtered = filtered.filter(s => {
        const reach = parseReachNumber(s.reach);
        if (min !== null && reach < min) return false;
        if (max !== null && reach > max) return false;
        return true;
      });
    }
    
    // Filter by type (vip/pago or free)
    if (filterType) {
      const types = filterType.split(',');
      filtered = filtered.filter(s => types.includes(s.service_type?.toLowerCase()));
    }
    
    setServices(filtered);
    
    // Update creators from filtered services
    const uniqueCreators = [];
    const seenIds = new Set();
    for (const service of filtered) {
      if (service.seller && !seenIds.has(service.seller.id)) {
        seenIds.add(service.seller.id);
        uniqueCreators.push({
          ...service.seller,
          service: service
        });
      }
    }
    setCreators(uniqueCreators);
  }, [filterPlatform, filterSubscribers, filterType, allServices]);

  const fetchData = async () => {
    try {
      // Fetch services for marketplace
      const servicesRes = await axios.get(`${API}/services/public?limit=50`);
      const servicesData = servicesRes.data.services || [];
      setAllServices(servicesData);
      setServices(servicesData);
      
      // Extract unique creators
      const uniqueCreators = [];
      const seenIds = new Set();
      for (const service of servicesData) {
        if (service.seller && !seenIds.has(service.seller.id)) {
          seenIds.add(service.seller.id);
          uniqueCreators.push({
            ...service.seller,
            service: service
          });
        }
      }
      setCreators(uniqueCreators);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (action, item) => {
    setPendingAction({ action, item });
    setShowLoginModal(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error("Preencha todos os campos");
      return;
    }
    
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword
      });
      
      setAuth(response.data.token, response.data.user);
      toast.success("Login realizado com sucesso!");
      setShowLoginModal(false);
      
      // Handle pending action or redirect
      if (response.data.user.is_admin) {
        navigate("/admin");
      } else if (pendingAction) {
        // Redirect based on action
        if (pendingAction.action === "marketplace") {
          navigate(`/s/${pendingAction.item.id}`);
        } else if (pendingAction.action === "exchange") {
          navigate("/exchanges");
        } else {
          navigate("/home");
        }
      } else {
        navigate("/home");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "E-mail ou senha incorretos");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword || !registerName) {
      toast.error("Preencha todos os campos");
      return;
    }
    
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: loginEmail,
        password: loginPassword,
        display_name: registerName
      });
      
      setAuth(response.data.token, response.data.user);
      toast.success("Conta criada com sucesso!");
      setShowLoginModal(false);
      navigate("/onboarding");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar conta");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Digite seu e-mail");
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      toast.error("Erro ao enviar e-mail");
    } finally {
      setForgotLoading(false);
    }
  };

  const getPhoto = (item) => {
    const photos = item?.photos || item?.seller?.photos || [];
    if (photos.length > 0) {
      return photos[0].startsWith('http') ? photos[0] : `${process.env.REACT_APP_BACKEND_URL}${photos[0]}`;
    }
    return null;
  };

  // Get badge for service card
  const getServiceBadge = (service, index) => {
    // Top 3 most ordered = "Mais Vendido"
    if (service.total_orders >= 10 || index < 3) {
      if (index === 0) return { text: "🔥 Mais Vendido", color: "bg-orange-500" };
    }
    // Fast delivery
    if (service.delivery_time === "24h" || service.delivery_time === "12h") {
      if (index === 3 || index === 4) return { text: "⚡ Entrega Rápida", color: "bg-blue-500" };
    }
    // High rating
    if (service.avg_rating >= 4.8 || service.seller?.trust_score >= 4.8) {
      if (index === 5) return { text: "⭐ Top Avaliado", color: "bg-yellow-500" };
    }
    // New (show for some services)
    if (index >= 10) return { text: "🆕 Novo", color: "bg-emerald-500" };
    return null;
  };

  const tabs = [
    { id: "marketplace", label: "Divulgação Paga", icon: ShoppingBag, color: "from-violet-500 to-purple-600" },
    { id: "exchanges", label: "Trocas Gratuitas", icon: RefreshCw, color: "from-emerald-500 to-teal-600", badge: "GRÁTIS" },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Gradient background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FD5564]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-[120px]" />
      </div>

      {/* Header */}
      <header className="relative z-50 px-4 py-4 border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <AltalabLogo size="default" clickable={false} />
          <Button
            onClick={() => setShowLoginModal(true)}
            className="bg-white/10 hover:bg-white/20 text-white border border-white/10"
          >
            Entrar
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 pt-12 pb-6 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#FD5564]" />
              <span className="text-sm text-white/70">A plataforma dos criadores de conteúdo</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              Divulgue seu conteúdo para{" "}
              <span className="bg-gradient-to-r from-[#FD5564] via-[#FF7854] to-violet-500 bg-clip-text text-transparent">
                milhares de pessoas
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 mb-8 max-w-2xl mx-auto">
              Compre, venda ou troque divulgação com outros criadores.
            </p>

            {/* CTA Button */}
            <Button
              onClick={() => setShowLoginModal(true)}
              size="lg"
              className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-lg shadow-[#FD5564]/25"
            >
              Começar Agora
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Trust Bar - Diferenciais */}
      <section className="relative z-10 py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4 md:gap-8"
          >
            {[
              { icon: CheckCircle, text: "100% Gratuito para começar" },
              { icon: Shield, text: "Criadores verificados" },
              { icon: RefreshCw, text: "Trocas sem custo" },
              { icon: Lock, text: "Pagamento seguro" },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-white/60">
                <item.icon className="w-4 h-4 text-emerald-400" />
                <span className="text-sm">{item.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="relative z-10 px-4 mb-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-center">
            <div className="inline-flex bg-white/5 border border-white/10 rounded-2xl p-1.5 gap-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
                      isActive
                        ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                        : "text-white/60 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    {tab.badge && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        isActive ? "bg-white/20" : "bg-emerald-500/20 text-emerald-400"
                      }`}>
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Content Area */}
      <section className="relative z-10 px-4 pb-20">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Marketplace Tab */}
            {activeTab === "marketplace" && (
              <motion.div
                key="marketplace"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">Impulsione seu Perfil Agora</h2>
                    <p className="text-white/50">Escolha um criador e apareça para milhares de pessoas</p>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/40">
                    <TrendingUp className="w-4 h-4" />
                    {services.length} serviços
                  </div>
                </div>

                {/* Filters Bar */}
                <div className="flex flex-wrap gap-3 mb-6 p-4 bg-white/[0.02] border border-white/10 rounded-xl">
                  <div className="flex items-center gap-2 text-sm text-white/60">
                    <Filter className="w-4 h-4" />
                    Filtros:
                  </div>
                  
                  {/* Platform Filter */}
                  <div className="relative">
                    <select
                      value={filterPlatform}
                      onChange={(e) => setFilterPlatform(e.target.value)}
                      className="appearance-none bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 pr-8 text-sm text-white focus:outline-none focus:border-[#FD5564]/50 cursor-pointer"
                    >
                      {PLATFORMS.map(p => (
                        <option key={p.value} value={p.value} className="bg-[#1a1a1a]">{p.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                  </div>
                  
                  {/* Subscribers Filter */}
                  <div className="relative">
                    <select
                      value={filterSubscribers}
                      onChange={(e) => setFilterSubscribers(e.target.value)}
                      className="appearance-none bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 pr-8 text-sm text-white focus:outline-none focus:border-[#FD5564]/50 cursor-pointer"
                    >
                      {SUBSCRIBER_RANGES.map(r => (
                        <option key={r.value} value={r.value} className="bg-[#1a1a1a]">{r.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                  </div>
                  
                  {/* Type Filter */}
                  <div className="relative">
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="appearance-none bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 pr-8 text-sm text-white focus:outline-none focus:border-[#FD5564]/50 cursor-pointer"
                    >
                      {PROFILE_TYPES.map(t => (
                        <option key={t.value} value={t.value} className="bg-[#1a1a1a]">{t.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                  </div>
                  
                  {/* Clear filters */}
                  {(filterPlatform || filterSubscribers || filterType) && (
                    <button
                      onClick={() => {
                        setFilterPlatform("");
                        setFilterSubscribers("");
                        setFilterType("");
                      }}
                      className="text-sm text-[#FD5564] hover:text-[#FF7854] transition-colors"
                    >
                      Limpar filtros
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {services.slice(0, 12).map((service, index) => {
                    const badge = getServiceBadge(service, index);
                    return (
                    <motion.div
                      key={service.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="group relative bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-white/20 rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
                      onClick={() => navigate(`/seller/${service.seller?.id}`)}
                      data-testid={`service-card-${service.id}`}
                    >
                      {/* Badge */}
                      {badge && (
                        <div className={`absolute top-3 right-3 z-10 px-2 py-1 rounded-full text-[10px] font-bold text-white ${badge.color}`}>
                          {badge.text}
                        </div>
                      )}
                      
                      {/* Creator Header */}
                      <div className="p-4 pb-3">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="w-12 h-12 rounded-full overflow-hidden bg-white/10">
                              {getPhoto(service) ? (
                                <img src={getPhoto(service)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Users className="w-5 h-5 text-white/30" />
                                </div>
                              )}
                            </div>
                            {service.seller?.is_verified && (
                              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold truncate">{service.seller?.display_name}</p>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="flex items-center gap-1 text-yellow-400">
                                <Star className="w-3 h-3 fill-current" />
                                {service.seller?.trust_score?.toFixed(1) || "4.9"}
                              </span>
                              <span className="text-white/30">•</span>
                              <span className="text-white/50 truncate">
                                {service.seller?.location?.city || "Brasil"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Service Info */}
                      <div className="px-4 pb-4">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {/* Platform + Type Badge */}
                          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium ${getPlatformColor(service.platform)}`}>
                            {getPlatformIcon(service.platform)}
                            <span>{service.platform?.toUpperCase()}</span>
                            {service.service_type && (
                              <>
                                <span className="opacity-50">•</span>
                                <span className="flex items-center gap-0.5">
                                  {['vip', 'pago'].includes(service.service_type?.toLowerCase()) && (
                                    <Crown className="w-3 h-3" />
                                  )}
                                  {service.service_type?.toUpperCase()}
                                </span>
                              </>
                            )}
                          </div>
                          {/* Subscribers Badge */}
                          {service.reach && (
                            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-[#FD5564]/20 text-[#FD5564]">
                              <Users className="w-3 h-3" />
                              {service.reach}
                            </div>
                          )}
                        </div>
                        {/* Title - promotional text */}
                        {service.title && (
                          <h3 className="font-semibold mb-2 line-clamp-1 group-hover:text-[#FD5564] transition-colors">
                            {service.title}
                          </h3>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xl font-bold text-emerald-400">
                            R$ {service.price?.toFixed(2)}
                          </span>
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); navigate(`/seller/${service.seller?.id}`); }}
                            className="bg-gradient-to-r from-violet-500 to-purple-600 hover:opacity-90 text-white text-xs"
                          >
                            Ver Pacotes
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                    );
                  })}
                </div>
                
                {/* No results */}
                {services.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <ShoppingBag className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50 mb-2">Nenhum serviço encontrado</p>
                    <button
                      onClick={() => {
                        setFilterPlatform("");
                        setFilterSubscribers("");
                        setFilterType("");
                      }}
                      className="text-[#FD5564] hover:underline"
                    >
                      Limpar filtros
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Exchanges Tab */}
            {activeTab === "exchanges" && (
              <motion.div
                key="exchanges"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      Divulgue Grátis
                      <span className="text-sm px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">100% FREE</span>
                    </h2>
                    <p className="text-white/50">Você divulga, eu divulgo. Simples assim.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  {creators.slice(0, 10).map((creator, index) => (
                    <motion.div
                      key={creator.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="group bg-gradient-to-b from-emerald-500/5 to-transparent border border-emerald-500/20 hover:border-emerald-500/40 rounded-2xl p-4 text-center transition-all duration-300"
                    >
                      <div className="relative w-20 h-20 mx-auto mb-3">
                        <div className="w-full h-full rounded-full overflow-hidden bg-white/10 ring-2 ring-emerald-500/30">
                          {getPhoto(creator) ? (
                            <img src={getPhoto(creator)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Users className="w-8 h-8 text-white/30" />
                            </div>
                          )}
                        </div>
                        <div className="absolute -bottom-1 right-1 px-2 py-0.5 bg-emerald-500 rounded-full text-[10px] font-bold">
                          FREE
                        </div>
                      </div>
                      
                      <h3 className="font-semibold mb-1 truncate">{creator.display_name}</h3>
                      <p className="text-sm text-white/40 mb-2 flex items-center justify-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {creator.location?.city || "Brasil"}
                      </p>
                      
                      {/* Platform and Reach from service */}
                      {creator.service && (
                        <div className="flex flex-col items-center gap-1 mb-3">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${getPlatformColor(creator.service.platform)}`}>
                            {creator.service.platform?.toUpperCase()}
                          </span>
                          {creator.service.reach && (
                            <span className="text-[10px] px-2 py-0.5 bg-[#FD5564]/20 text-[#FD5564] rounded-full font-semibold">
                              {creator.service.reach}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <Button
                        size="sm"
                        onClick={() => handleAction("exchange", creator)}
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Trocar
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative z-10 py-16 px-4 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">Como Funciona</h2>
            <p className="text-white/50">Três passos simples para crescer sua audiência</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                icon: Eye,
                title: "Explore a Vitrine",
                description: "Navegue por criadores de Privacy, OnlyFans, Telegram e mais. Veja alcance, preços e avaliações antes de decidir.",
                color: "from-violet-500 to-purple-600"
              },
              {
                step: "2",
                icon: Sparkles,
                title: "Escolha sua Estratégia",
                description: "Divulgação Paga: compre posts e stories de outros criadores. Trocas Gratuitas: divulgue sem gastar nada.",
                color: "from-emerald-500 to-teal-600"
              },
              {
                step: "3",
                icon: TrendingUp,
                title: "Cresça sua Audiência",
                description: "Acompanhe resultados, avalie criadores e construa parcerias de longo prazo para crescimento contínuo.",
                color: "from-[#FD5564] to-[#FF7854]"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative bg-white/[0.02] border border-white/10 rounded-2xl p-6 text-center"
              >
                <div className={`w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-sm font-bold text-white/40">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative z-10 py-16 px-4 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">O que estão dizendo</h2>
            <p className="text-white/50">Criadores que já estão crescendo com a gente</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "Consegui 3 parcerias incríveis na primeira semana! A plataforma é muito intuitiva.",
                name: "Bella Cruz",
                handle: "@bellacruz",
                photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
              },
              {
                quote: "As trocas gratuitas mudaram meu jogo. Zero investimento, muito resultado.",
                name: "Lucas Top",
                handle: "@lucastop",
                photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop"
              },
              {
                quote: "Finalmente um lugar focado em criadores como a gente. Adorei a experiência!",
                name: "Amanda VIP",
                handle: "@amandavip",
                photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/[0.02] border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src={testimonial.photo} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-[#FD5564]">{testimonial.handle}</p>
                  </div>
                </div>
                <p className="text-white/70 italic">"{testimonial.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative z-10 py-16 px-4 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Pronto para{" "}
              <span className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] bg-clip-text text-transparent">
                crescer?
              </span>
            </h2>
            <p className="text-lg text-white/50 mb-8">
              Junte-se a criadores que estão levando suas carreiras para o próximo nível.
            </p>
            <Button
              onClick={() => setShowLoginModal(true)}
              size="lg"
              className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white font-semibold px-10 py-6 text-lg rounded-xl shadow-lg shadow-[#FD5564]/25"
            >
              Criar Conta Grátis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Login Modal */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowLoginModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-3xl overflow-hidden shadow-2xl"
            >
              {/* Close button */}
              <button
                onClick={() => setShowLoginModal(false)}
                className="absolute top-4 right-4 p-2 text-white/40 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-8">
                {!showForgotPassword ? (
                  <>
                    {/* Logo */}
                    <div className="flex justify-center mb-6">
                      <AltalabLogo size="default" clickable={false} />
                    </div>

                    <h2 className="text-2xl font-bold text-center mb-2">
                      {isRegister ? "Criar conta" : "Bem-vindo de volta"}
                    </h2>
                    <p className="text-center text-white/50 mb-6">
                      {isRegister 
                        ? "Crie sua conta para continuar" 
                        : "Entre para continuar"}
                    </p>

                    <form onSubmit={isRegister ? handleRegister : handleLogin} className="space-y-4">
                      {isRegister && (
                        <div className="space-y-2">
                          <Label className="text-white/70">Nome</Label>
                          <Input
                            placeholder="Seu nome"
                            value={registerName}
                            onChange={(e) => setRegisterName(e.target.value)}
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                          />
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <Label className="text-white/70">E-mail</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                          <Input
                            type="email"
                            placeholder="seu@email.com"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-white/70">Senha</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {!isRegister && (
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="text-sm text-white/50 hover:text-[#FD5564] transition-colors"
                          >
                            Esqueci minha senha
                          </button>
                        </div>
                      )}
                      
                      <Button
                        type="submit"
                        disabled={loginLoading}
                        className="w-full h-12 bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white font-semibold"
                      >
                        {loginLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            {isRegister ? "Criar conta" : "Entrar"}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </form>

                    {/* Divider */}
                    <div className="flex items-center gap-4 my-6">
                      <div className="flex-1 h-px bg-white/10"></div>
                      <span className="text-white/40 text-sm">ou</span>
                      <div className="flex-1 h-px bg-white/10"></div>
                    </div>

                    {/* Google Login Button */}
                    {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                    <Button
                      type="button"
                      onClick={() => {
                        const redirectUrl = window.location.origin;
                        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                      }}
                      variant="outline"
                      className="w-full h-12 bg-white hover:bg-gray-100 text-gray-800 font-medium border-0"
                    >
                      <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continuar com Google
                    </Button>

                    <div className="mt-6 text-center">
                      <span className="text-white/50">
                        {isRegister ? "Já tem conta? " : "Não tem conta? "}
                      </span>
                      <button
                        onClick={() => setIsRegister(!isRegister)}
                        className="text-[#FD5564] hover:underline font-medium"
                      >
                        {isRegister ? "Fazer login" : "Criar conta grátis"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Forgot Password */}
                    <button
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotSent(false);
                      }}
                      className="flex items-center gap-1 text-white/50 hover:text-white mb-6 text-sm"
                    >
                      <ArrowRight className="w-4 h-4 rotate-180" />
                      Voltar
                    </button>

                    {!forgotSent ? (
                      <>
                        <h2 className="text-2xl font-bold mb-2">Esqueceu a senha?</h2>
                        <p className="text-white/50 mb-6">
                          Digite seu e-mail para receber o link de recuperação.
                        </p>

                        <form onSubmit={handleForgotPassword} className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-white/70">E-mail</Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                              <Input
                                type="email"
                                placeholder="seu@email.com"
                                value={forgotEmail}
                                onChange={(e) => setForgotEmail(e.target.value)}
                                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                              />
                            </div>
                          </div>

                          <Button
                            type="submit"
                            disabled={forgotLoading}
                            className="w-full h-12 bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white font-semibold"
                          >
                            {forgotLoading ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              "Enviar link"
                            )}
                          </Button>
                        </form>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                          <Mail className="w-8 h-8 text-emerald-400" />
                        </div>
                        <h2 className="text-xl font-bold mb-2">E-mail enviado!</h2>
                        <p className="text-white/50 mb-4">
                          Verifique sua caixa de entrada e spam.
                        </p>
                        <Button
                          onClick={() => {
                            setShowForgotPassword(false);
                            setForgotSent(false);
                          }}
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/5"
                        >
                          Voltar para login
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="relative z-10 py-8 px-4 border-t border-white/5 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <AltalabLogo size="small" clickable={false} />
          <p className="text-sm text-white/30">
            © 2024 altalab. Todos os direitos reservados.
          </p>
        </div>
      </footer>

      {/* Mobile Floating CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-[#050505] via-[#050505] to-transparent md:hidden">
        <Button
          onClick={() => setShowLoginModal(true)}
          className="w-full h-14 bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white font-semibold text-lg rounded-xl shadow-lg shadow-[#FD5564]/25"
        >
          Criar Conta Grátis
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default LandingPage;
